import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { Check, Copy, Download, Loader2, RefreshCw, Share2 } from "lucide-react";

import { ConnectProto } from "@racoon/api";
import { Namespace, useLang, useT } from "@racoon/i18n";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
  useToast,
} from "@racoon/ui";

import { useGetContactByIdQuery } from "~/shared/api/contact/contact.grpc";
import { buildShareReport, ShareReport, ShareReportPhoto } from "../model/share-report";
import { captureLayoutZoom, captureReportImage, loadReportPhoto } from "../lib/render-report-image";
import {
  canCopyImage,
  canNativeShare,
  copyImage,
  downloadImage,
  nativeShare,
} from "../lib/share-actions";
import { ReportCard } from "./report-card";

interface Props {
  job: ConnectProto.Job;
}

// How long the copy button reads «Скопировано!» after a successful copy.
const COPIED_FEEDBACK_MS = 3000;

type Capture =
  | { status: "loading" }
  | { status: "ready"; blob: Blob; previewUrl: string; fileName: string; jobNumber: string }
  | { status: "error" };

const ShareReportBody: FC<Props> = ({ job }) => {
  const t = useT();
  const { lang } = useLang();
  const { toast } = useToast();
  const cardRef = useRef<HTMLDivElement>(null);
  const [attempt, setAttempt] = useState(0);
  const [cardReport, setCardReport] = useState<ShareReport | null>(null);
  const [capture, setCapture] = useState<Capture>({ status: "loading" });
  // Bumped on every successful copy, so a second copy restarts the countdown.
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  // navigator.share settles only once the share sheet closes. The ref blocks a
  // second tap before the state re-renders — a second call would be rejected
  // (InvalidStateError) or open another sheet.
  const sharingRef = useRef(false);
  const [isSharing, setIsSharing] = useState(false);

  // The job only carries the provider's contact id, so the contact itself is
  // fetched here. Failing that, the card prints the provider name alone.
  const providerId = job.serviceProvider?.id || job.serviceProviderId;
  const primaryContactId = job.serviceProvider?.primaryContactId;
  const { data: contact, isLoading: isContactLoading } = useGetContactByIdQuery(
    primaryContactId && providerId
      ? { id: primaryContactId, ownerOrganizationId: providerId }
      : skipToken,
  );
  // Step 1: build the report and download its photos, then mount the card.
  useEffect(() => {
    if (isContactLoading) return;

    let cancelled = false;
    const objectUrls: string[] = [];
    setCapture({ status: "loading" });
    setCardReport(null);

    const loadPhoto = async (photo: ShareReportPhoto | null): Promise<ShareReportPhoto | null> => {
      if (!photo) return null;
      const url = await loadReportPhoto(photo.url);
      if (!url) return null;
      if (cancelled) URL.revokeObjectURL(url);
      else objectUrls.push(url);
      return { ...photo, url };
    };

    (async () => {
      const report = buildShareReport(job, { contact, lang });
      const [before, after] = await Promise.all([
        loadPhoto(report.photos.before),
        loadPhoto(report.photos.after),
      ]);
      if (cancelled) return;

      if ((report.photos.before && !before) || (report.photos.after && !after)) {
        toast({
          title: t(Namespace.PhotoReport, "share.photoLoadFailed"),
          description: t(Namespace.PhotoReport, "share.photoLoadFailedHint"),
        });
      }
      setCardReport({ ...report, photos: { before, after } });
    })().catch((error) => {
      console.error("Failed to build the photo report", error);
      if (!cancelled) setCapture({ status: "error" });
    });

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [isContactLoading, job, contact, lang, attempt, toast, t]);

  // Step 2: once the card is on the page, capture it.
  useEffect(() => {
    const node = cardRef.current;
    if (!cardReport || !node) return;

    let cancelled = false;
    captureReportImage(node).then(
      (blob) => {
        if (cancelled) return;
        setCapture({
          status: "ready",
          blob,
          previewUrl: URL.createObjectURL(blob),
          fileName: cardReport.fileName,
          jobNumber: cardReport.jobNumber,
        });
      },
      (error) => {
        console.error("Failed to capture the photo report", error);
        if (!cancelled) setCapture({ status: "error" });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [cardReport]);

  useEffect(
    () => () => {
      if (capture.status === "ready") URL.revokeObjectURL(capture.previewUrl);
    },
    [capture],
  );

  useEffect(() => {
    if (copiedAt === null) return;
    const timer = setTimeout(() => setCopiedAt(null), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copiedAt]);

  const handleCopy = async (blob: Blob) => {
    try {
      await copyImage(blob);
      setCopiedAt(Date.now());
    } catch (error) {
      console.error("Failed to copy the photo report", error);
      toast({ title: t(Namespace.PhotoReport, "share.copyFailed"), variant: "destructive" });
    }
  };

  const handleNativeShare = async (blob: Blob, fileName: string) => {
    if (sharingRef.current) return;
    sharingRef.current = true;
    setIsSharing(true);
    try {
      await nativeShare(blob, fileName);
    } catch (error) {
      console.error("Failed to share the photo report", error);
      toast({ title: t(Namespace.PhotoReport, "share.shareFailed"), variant: "destructive" });
    } finally {
      sharingRef.current = false;
      setIsSharing(false);
    }
  };

  const ready = capture.status === "ready" ? capture : null;

  return (
    <>
      {/* The card brings its own padding, so the preview spans the dialog edge
          to edge and scrolls rather than shrinking to fit the screen height. */}
      <ModalBody className="min-h-0 flex-1 overflow-y-auto !p-0">
        {capture.status === "loading" && (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-text-secondary">
            <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
            <div className="text-sm">{t(Namespace.PhotoReport, "share.generating")}</div>
          </div>
        )}
        {capture.status === "error" && (
          <div className="flex min-h-[320px] flex-col items-center justify-center gap-4">
            <div className="text-sm text-text-secondary">
              {t(Namespace.PhotoReport, "share.failed")}
            </div>
            <Button variant="opacityBlueWithoutOutline" onClick={() => setAttempt((n) => n + 1)}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t(Namespace.Buttons, "retry")}
            </Button>
          </div>
        )}
        {capture.status === "ready" && (
          <img
            data-testid="share-report-preview"
            src={capture.previewUrl}
            alt={t(Namespace.PhotoReport, "share.previewAlt", { jobNumber: capture.jobNumber })}
            className="block w-full"
          />
        )}
      </ModalBody>

      <ModalFooter className="flex-wrap">
        {ready && canNativeShare(ready.blob, ready.fileName) && (
          <Button
            variant="outline"
            data-testid="share-report-native-share"
            disabled={isSharing}
            onClick={() => handleNativeShare(ready.blob, ready.fileName)}
          >
            {isSharing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Share2 className="mr-2 h-4 w-4" />
            )}
            {t(Namespace.PhotoReport, "share.nativeShare")}
          </Button>
        )}
        <Button
          variant="outline"
          data-testid="share-report-download"
          disabled={!ready}
          onClick={() => ready && downloadImage(ready.blob, ready.fileName)}
        >
          <Download className="mr-2 h-4 w-4" />
          {t(Namespace.Common, "download")}
        </Button>
        {canCopyImage() && (
          <Button
            variant="solidBlue"
            data-testid="share-report-copy"
            disabled={!ready}
            onClick={() => ready && handleCopy(ready.blob)}
          >
            {copiedAt !== null ? (
              <Check className="mr-2 h-4 w-4" />
            ) : (
              <Copy className="mr-2 h-4 w-4" />
            )}
            {copiedAt !== null
              ? t(Namespace.PhotoReport, "share.copied")
              : t(Namespace.Buttons, "copy")}
          </Button>
        )}
      </ModalFooter>

      {cardReport &&
        capture.status === "loading" &&
        createPortal(
          // Off-screen but laid out, so the capture sees the card at full size.
          <div
            aria-hidden
            className="pointer-events-none fixed top-0"
            style={{ left: -10_000, zoom: captureLayoutZoom() }}
          >
            <ReportCard ref={cardRef} report={cardReport} />
          </div>,
          document.body,
        )}
    </>
  );
};

/**
 * «Поделиться» for the photo report modal: renders the job's report card to a
 * PNG and offers to copy, download or share it.
 */
export const ShareReportDialog: FC<Props & { triggerClassName?: string; disabled?: boolean }> = ({
  triggerClassName,
  disabled,
  ...props
}) => {
  const t = useT();
  const [open, setOpen] = useState(false);

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger asChild>
        <Button
          id="photo-report-share-button"
          data-testid="photo-report-share-button"
          variant="opacityBlueWithoutOutline"
          className={triggerClassName}
          disabled={disabled}
        >
          {t(Namespace.PhotoReport, "share.button")}
        </Button>
      </ModalTrigger>

      {/* 822px: REPORT_CARD_WIDTH plus the dialog's 1px borders, so the preview is 1:1 */}
      <ModalContent size="custom" className="md:!max-w-[min(92vw,822px)]">
        <ModalHeader>
          <ModalTitle>{t(Namespace.PhotoReport, "share.title")}</ModalTitle>
        </ModalHeader>
        {/* Content unmounts on close, so every open starts a fresh report. */}
        <ShareReportBody {...props} />
      </ModalContent>
    </Modal>
  );
};
